import { Link } from "react-router-dom";
import { SeoTags } from "../../../components/Head/SeoTags";
import OfflineMock from "../../../components/Mockups/OfflineMock";

const APP_URL = process.env.REACT_APP_APP_URL;

const Offline = () => {
  return (
    <>
      <SeoTags
        title="Offline Mode - Your Bookmarks Without a Connection"
        description="Browse your bookmarks, read saved articles, and even save new links with no internet connection. Changes queue locally and sync automatically when you're back online."
        keywords="offline bookmarks, bookmark manager offline, offline reading, PWA bookmark manager, offline sync, read bookmarks offline"
        canonical="https://onbookmarks.com/features/offline/"
        localized={false}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Offline Mode - Your Bookmarks Without a Connection",
            description:
              "Browse bookmarks, read saved articles, and save new links offline. Changes queue locally and sync automatically when the connection returns.",
            url: "https://onbookmarks.com/features/offline/",
            mainEntity: {
              "@type": "SoftwareApplication",
              name: "onBookmarks",
              applicationCategory: "ProductivityApplication",
              operatingSystem: "Web, iOS, Android",
              featureList: [
                "Browse cached bookmarks and collections offline",
                "Read saved article content offline",
                "Save, edit, and delete bookmarks offline",
                "Automatic background sync with retries",
                "Installable PWA",
              ],
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://onbookmarks.com" },
                { "@type": "ListItem", position: 2, name: "Features", item: "https://onbookmarks.com/features/" },
                { "@type": "ListItem", position: 3, name: "Offline Mode", item: "https://onbookmarks.com/features/offline/" },
              ],
            },
          }),
        }}
      />

      {/* Hero */}
      <section className="hero bg-purple-subtle">
        <div className="container">
          <div className="row align-items-center min-vh-50">
            <div className="col-lg-6">
              <div className="d-flex flex-column gap-4">
                <div>
                  <span className="badge bg-purple text-white px-3 py-2">
                    <i className="bi bi-wifi-off me-2"></i>
                    Works Without Internet
                  </span>
                </div>
                <h1 className="display-4 fw-bold">
                  Your bookmarks, even with no connection
                </h1>
                <p className="lead">
                  On a plane, on the subway, in a dead zone. Your library stays
                  browsable, your saved articles stay readable, and anything you
                  save syncs the moment you're back online.
                </p>
                <div className="d-flex gap-3 flex-wrap">
                  <Link
                    to={`${APP_URL}/register`}
                    className="btn btn-primary btn-lg px-4"
                  >
                    Start Free
                  </Link>
                  <a
                    href="#how-it-works"
                    className="btn btn-outline-primary btn-lg px-4"
                  >
                    See How It Works
                  </a>
                </div>
              </div>
            </div>
            <div className="col-lg-6 mt-5 mt-lg-0">
              <OfflineMock />
            </div>
          </div>
        </div>
      </section>

      {/* What works offline */}
      <section className="py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center mb-5">
              <h2 className="h1 fw-bold mb-4">
                Real offline mode, not a cached splash screen
              </h2>
              <p className="lead text-muted">
                Many apps call themselves "offline-ready" because the logo loads
                without internet. onBookmarks keeps your actual data usable.
              </p>
            </div>
          </div>
          <div className="row g-4">
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <i className="bi bi-collection fs-3 text-purple mb-3"></i>
                  <h3 className="h5 mb-2">Browse your library</h3>
                  <p className="text-muted mb-0">
                    Bookmarks and collections you've loaded are cached on your
                    device. Search them, open them, organize them offline.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <i className="bi bi-book fs-3 text-purple mb-3"></i>
                  <h3 className="h5 mb-2">Read saved articles</h3>
                  <p className="text-muted mb-0">
                    Saved article content stays readable offline. Perfect for
                    flights and commutes. Load it once, read it anywhere.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <i className="bi bi-arrow-repeat fs-3 text-purple mb-3"></i>
                  <h3 className="h5 mb-2">Save now, sync later</h3>
                  <p className="text-muted mb-0">
                    Add, edit, or delete bookmarks offline. Every change is
                    queued locally and replayed automatically (with retries)
                    when your connection returns.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="h1 fw-bold mb-4">How it works</h2>
            <p className="lead text-muted">
              Built on modern web standards. No extra app to install (unless
              you want to).
            </p>
          </div>
          <div className="row g-4">
            <div className="col-md-4">
              <div className="text-center">
                <div
                  className="rounded-circle bg-purple-subtle d-inline-flex align-items-center justify-content-center mb-3 step-circle"
                >
                  <span className="fs-2 fw-bold text-purple">1</span>
                </div>
                <h3 className="h5 mb-3">Use the app normally</h3>
                <p className="text-muted">
                  As you browse, your bookmarks, collections, and saved content
                  are cached locally in your browser, automatically.
                </p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="text-center">
                <div
                  className="rounded-circle bg-purple-subtle d-inline-flex align-items-center justify-content-center mb-3 step-circle"
                >
                  <span className="fs-2 fw-bold text-purple">2</span>
                </div>
                <h3 className="h5 mb-3">Go offline</h3>
                <p className="text-muted">
                  Lose the connection and nothing breaks. The app serves your
                  library from the local cache and quietly queues your changes.
                </p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="text-center">
                <div
                  className="rounded-circle bg-purple-subtle d-inline-flex align-items-center justify-content-center mb-3 step-circle"
                >
                  <span className="fs-2 fw-bold text-purple">3</span>
                </div>
                <h3 className="h5 mb-3">Reconnect and sync</h3>
                <p className="text-muted">
                  Back online, queued changes sync in the background with
                  automatic retries. You'll see the sync status in the app.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Install as app */}
      <section className="py-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <h2 className="h1 fw-bold mb-4">Install it like a native app</h2>
              <p className="lead mb-4">
                onBookmarks is an installable PWA. Add it to your home screen
                or desktop and it launches instantly, connection or not.
              </p>
              <ul className="list-unstyled">
                <li className="d-flex align-items-start mb-3">
                  <i className="bi bi-check-circle-fill text-success fs-5 me-3 mt-1"></i>
                  <div>
                    <h4 className="h6 mb-1">Works on every platform</h4>
                    <p className="text-muted mb-0">
                      Desktop, iOS, and Android: one app, no app store needed.
                    </p>
                  </div>
                </li>
                <li className="d-flex align-items-start mb-3">
                  <i className="bi bi-check-circle-fill text-success fs-5 me-3 mt-1"></i>
                  <div>
                    <h4 className="h6 mb-1">Instant startup</h4>
                    <p className="text-muted mb-0">
                      The app shell is cached, so it opens immediately even on a
                      flaky connection.
                    </p>
                  </div>
                </li>
                <li className="d-flex align-items-start mb-3">
                  <i className="bi bi-check-circle-fill text-success fs-5 me-3 mt-1"></i>
                  <div>
                    <h4 className="h6 mb-1">Honest limits</h4>
                    <p className="text-muted mb-0">
                      Offline mode serves what your device has cached: bookmarks
                      you've never loaded on that device sync down when you
                      reconnect.
                    </p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="col-lg-6">
              <div className="bg-light rounded-4 p-5 text-center">
                <i className="bi bi-phone display-1 text-purple mb-3"></i>
                <p className="h5 mb-2">Add to Home Screen</p>
                <p className="text-muted mb-0">
                  Your whole library, one tap away, with or without a signal.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-5">
        <div className="container">
          <div className="bg-purple rounded-4 p-5 text-center">
            <h2 className="h1 fw-bold mb-4 text-white">
              Never lose access to your own library
            </h2>
            <p className="lead mb-4 text-white">
              The most-requested bookmark feature on the internet, working here
              today.
            </p>
            <Link to={`${APP_URL}/register`} className="btn btn-light btn-lg px-5">
              Try Offline Mode Free
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Offline;
